import { useEffect } from 'react'
import { Alert, Form, Input, InputNumber, Modal, Switch } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { SKU_PATTERN, createStockItem, updateStockItem, type StockItem } from './stockApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface StockItemFormModalProps {
  open: boolean
  /** `null` = yangi mol turi */
  item: StockItem | null
  onClose: () => void
}

interface FormValues {
  sku: string
  name: string
  unit: string
  minQuantity: number
  active: boolean
}

/**
 * Mol turi (miqdorli) — yaratish va tahrirlash.
 *
 * <h2>⚠️ QOLDIQ MAYDONI YO'Q va hech qachon bo'lmaydi</h2>
 * `StockItemDraft` javadoc'i: qoldiq FAQAT HARAKAT orqali o'zgaradi
 * (`inventory.stock_balance` ni faqat trigger yozadi). "Boshlang'ich
 * qoldiq" ham harakat — kirim yoki inventarizatsiya tuzatishi. Aks
 * holda jurnalsiz paydo bo'lgan mol hisobotda tushuntirib bo'lmaydigan
 * farq berardi.
 *
 * <h2>⚠️ MOL TURI ≠ JIHOZ</h2>
 * Bu MIQDORLI tur (kabel, konnektor). Seriya raqamli fizik nusxa —
 * boshqa model va boshqa sahifa (`/inventory`). 500 metr kabelning
 * "seriya raqami" yo'q va bo'lishi ham mumkin emas
 * (`StockItem` javadoc'i, `QARORLAR.md` §20.8).
 *
 * <h2>⚠️ MINIMAL QOLDIQ — OGOHLANTIRISH, TAQIQ EMAS</h2>
 * Chiqim to'xtatilmaydi (operator mijoz oldida turgan bo'lishi mumkin).
 * Ogohlantirish chiqim AMALINING ICHIDA hisoblanadi, `@Scheduled` job
 * bilan emas (`QARORLAR.md` §20.1).
 *
 * <h2>⚠️ KASR MIQDOR QO'LLAB-QUVVATLANMAYDI</h2>
 * Kerak bo'lsa maydaroq birlik tanlanadi ("metr" → "santimetr").
 * `NUMERIC` har hisobotda yaxlitlash savolini ochardi — shuning uchun
 * bu yerda `precision={0}`.
 *
 * <h2>⚠️ XATO OYNANING ICHIDA QOLADI</h2>
 * `VoucherVoidModal` naqshi. Ilgari xabar SAHIFA banneriga chiqarilardi,
 * oyna esa ochiq qolardi — ya'ni matn modal niqobi ortida turardi va
 * operator faqat spinner to'xtaganini ko'rardi. Amal tugmasi bilan
 * xabar bir ekranda bo'lishi shart.
 */
export function StockItemFormModal({ open, item, onClose }: StockItemFormModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  const isEdit = item !== null

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      item
        ? {
            sku: item.sku,
            name: item.name,
            unit: item.unit,
            minQuantity: item.minQuantity,
            active: item.active,
          }
        : { sku: '', name: '', unit: '', minQuantity: 0, active: true },
    )
  }, [open, item, form])

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const draft = {
        sku: values.sku.trim(),
        name: values.name.trim(),
        unit: values.unit.trim(),
        minQuantity: values.minQuantity,
        // ⚠️ HAR DOIM yuboriladi — sabab `StockItemDraft` da (`Boolean`,
        // primitiv emas: tushib qolgan maydon molni JIMGINA nofaol
        // qilib qo'yardi).
        active: values.active,
      }
      return isEdit && item ? updateStockItem(item.id, draft) : createStockItem(draft)
    },
    onSuccess: () => {
      // ⚠️ `stock.all` PREFIKS: mol nomi qoldiqlar va jurnal
      // jadvallarida ham ko'rinadi va ular ham eskiradi.
      void queryClient.invalidateQueries({ queryKey: queryKeys.stock.all })
      form.resetFields()
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={isEdit ? t('stock.item.editTitle') : t('stock.item.createTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('stock.item.noBalanceNote')} />

        {/* 409 (takroriy SKU) va boshqalar SHU YERDA qoladi */}
        {save.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(save.error, t)}
          />
        ) : null}

        <Form.Item
          name="name"
          label={t('stock.item.name')}
          rules={[
            { required: true, message: t('stock.item.validation.nameRequired') },
            { max: 200, message: t('stock.item.validation.nameLength') },
          ]}
        >
          <Input autoFocus />
        </Form.Item>

        <Form.Item
          name="sku"
          label={t('stock.item.sku')}
          extra={t('stock.item.skuHint')}
          rules={[
            { required: true, message: t('stock.item.validation.skuRequired') },
            // `StockItemDraft` dagi `@Pattern` ning nusxasi.
            { pattern: SKU_PATTERN, message: t('stock.item.validation.skuFormat') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="unit"
          label={t('stock.item.unit')}
          extra={t('stock.item.unitHint')}
          rules={[
            { required: true, message: t('stock.item.validation.unitRequired') },
            { max: 32, message: t('stock.item.validation.unitLength') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="minQuantity"
          label={t('stock.item.minQuantity')}
          extra={t('stock.item.minQuantityHint')}
          rules={[{ required: true, message: t('stock.item.validation.minRequired') }]}
        >
          <InputNumber min={0} precision={0} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="active"
          label={t('stock.item.active')}
          valuePropName="checked"
          extra={t('stock.item.activeHint')}
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
